/** Privacy-safe, best-effort portal performance telemetry (AIC-336). */
import type { CLSMetric, INPMetric, LCPMetric } from "web-vitals";
import { PORTAL_ROUTE_TEMPLATES, type PortalRouteTemplate } from "@swtracker/core/telemetry/portal-routes";
import { NAV_TYPES, VITAL_BOUNDS, VITALS_MAX_BYTES, VITALS_MAX_SAMPLES, VITALS_ROUTE, type NavType, type VitalName, type VitalSample } from "@swtracker/core/telemetry/vitals";
import type { Auth } from "./auth/session.ts";

declare const __PORTAL_BUILD_SHA__: string;
type Metric = CLSMetric | INPMetric | LCPMetric;
type Unsubscribe = void | (() => void);
type Vitals = { onCLS(cb: (metric: CLSMetric) => void, options: { reportAllChanges: false }): Unsubscribe; onINP(cb: (metric: INPMetric) => void, options: { reportAllChanges: false }): Unsubscribe; onLCP(cb: (metric: LCPMetric) => void, options: { reportAllChanges: false }): Unsubscribe };
type Deps = { now(): number; fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>; navigationType(): NavType; vitals: Vitals; document?: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">; window?: Pick<Window, "addEventListener" | "removeEventListener"> };

const buildSha = () => typeof __PORTAL_BUILD_SHA__ === "string" && /^[0-9a-f]{7,40}$/.test(__PORTAL_BUILD_SHA__) ? __PORTAL_BUILD_SHA__ : null;
export const portalRouteTemplate = (path: string): PortalRouteTemplate | null => {
  const clean = new URL(path, typeof location === "undefined" ? "https://portal.invalid" : location.href).pathname.replace(/\/+$/, "") || "/";
  return PORTAL_ROUTE_TEMPLATES.find((t) => {
    const a = t.split("/"); const b = clean.split("/");
    return a.length === b.length && a.every((part, i) => part.startsWith(":") ? Boolean(b[i]) : part === b[i]);
  }) ?? null;
};
const valid = (name: VitalName, value: number) => Number.isFinite(value) && value >= VITAL_BOUNDS[name].min && value <= VITAL_BOUNDS[name].max;
const navValid = (nav: string): nav is NavType => (NAV_TYPES as readonly string[]).includes(nav);

export class PortalTelemetry {
  private samples: VitalSample[] = [];
  private documentVitals = new Map<Exclude<VitalName, "route_ready">, VitalSample>();
  private current: { path: string; generation: number; template: PortalRouteTemplate; started: number; nav: NavType; sent: boolean } | null = null;
  private generation = 0;
  private disposed = false;
  private flushing = false;
  private scheduled: ReturnType<typeof setTimeout> | null = null;
  private readonly reportedVitals = new Set<string>();
  private readonly cleanup: Array<() => void> = [];
  private readonly documentRoute = typeof location === "undefined" ? null : portalRouteTemplate(location.pathname);

  constructor(private readonly auth: Pick<Auth, "getToken">, private readonly deps: Deps = browserDeps()) {
    if (buildSha()) this.observeDocumentVitals();
    const visibility = () => { if (this.deps.document?.visibilityState === "hidden") void this.flush(); };
    // Known limit: flush awaits getToken() before its keepalive fetch, so a pagehide flush can lose the race when the token is not cached.
    const pagehide = () => { void this.flush(); };
    this.deps.document?.addEventListener("visibilitychange", visibility); this.deps.window?.addEventListener("pagehide", pagehide);
    this.cleanup.push(() => this.deps.document?.removeEventListener("visibilitychange", visibility), () => this.deps.window?.removeEventListener("pagehide", pagehide));
  }
  beginRoute(path: string, inApp: boolean, started = this.deps.now(), replace = false): void {
    const pathname = new URL(path, location.href).pathname.replace(/\/+$/, "") || "/";
    if (this.current?.path === pathname) return; // URL filters and anchors are not a navigation sample.
    const template = portalRouteTemplate(path);
    const pendingRedirect = replace && this.current && !this.current.sent ? this.current : null;
    this.current = template && buildSha() ? { path: pathname, generation: ++this.generation, template,
      started: pendingRedirect?.started ?? started, nav: pendingRedirect?.nav ?? (inApp ? "spa" : this.deps.navigationType()), sent: false } : null;
  }
  markReady(): void { if (this.current) this.ready(this.current.generation); }
  private ready(generation: number): void {
    const current = this.current;
    if (!current || current.generation !== generation || current.sent || this.disposed) return;
    current.sent = true; this.record("route_ready", this.deps.now() - current.started, current.template, current.nav); this.scheduleFlush();
  }
  dispose(): void { if (this.scheduled !== null) clearTimeout(this.scheduled); this.scheduled = null; this.disposed = true; this.generation++; this.samples = []; this.documentVitals.clear(); this.current = null; for (const cleanup of this.cleanup.splice(0)) cleanup(); }
  async flush(): Promise<void> {
    if (this.disposed || this.flushing || (!this.samples.length && !this.documentVitals.size)) return;
    if (this.scheduled !== null) clearTimeout(this.scheduled);
    this.scheduled = null;
    const samples = [...this.documentVitals.values(), ...this.samples.splice(0)].slice(0, VITALS_MAX_SAMPLES);
    this.documentVitals.clear(); this.flushing = true;
    try {
      const body = { samples }; if (new TextEncoder().encode(JSON.stringify(body)).byteLength > VITALS_MAX_BYTES) return;
      const token = await this.auth.getToken();
      if (!token || this.disposed) return;
      await this.deps.fetch(VITALS_ROUTE, { method: "POST", cache: "no-store", keepalive: true, headers: { authorization: `Bearer ${token}`, accept: "application/json", "content-type": "application/json" }, body: JSON.stringify(body) });
    } catch { /* no retries and no auth/session side effects */ } finally {
      this.flushing = false;
      // Only newly queued samples are drained. The attempted batch is never retried.
      if (!this.disposed && (this.samples.length || this.documentVitals.size)) this.scheduleFlush();
    }
  }
  private record(name: VitalName, value: number, template: PortalRouteTemplate | null, nav: NavType): void {
    const sha = buildSha(); if (!sha || !template || !navValid(nav) || !valid(name, value) || this.samples.length >= VITALS_MAX_SAMPLES) return;
    this.samples.push({ name, value, route_template: template, nav_type: nav, build_sha: sha });
  }
  private documentVital(name: Exclude<VitalName, "route_ready">, metric: Metric): void {
    const sha = buildSha(); if (this.disposed || !sha || !this.documentRoute || !valid(name, metric.value) || !navValid(metric.navigationType)) return;
    const key = `${name}:${metric.id}`;
    if (this.reportedVitals.has(key)) return;
    this.reportedVitals.add(key);
    this.documentVitals.set(name, { name, value: metric.value, route_template: this.documentRoute, nav_type: metric.navigationType, build_sha: sha });
    this.scheduleFlush();
  }
  private observeDocumentVitals(): void {
    for (const unsubscribe of [
      this.deps.vitals.onLCP((m) => this.documentVital("LCP", m), { reportAllChanges: false }),
      this.deps.vitals.onINP((m) => this.documentVital("INP", m), { reportAllChanges: false }),
      this.deps.vitals.onCLS((m) => this.documentVital("CLS", m), { reportAllChanges: false }),
    ]) if (unsubscribe) this.cleanup.push(unsubscribe);
  }
  private scheduleFlush(): void { if (this.scheduled === null && !this.disposed) this.scheduled = setTimeout(() => { this.scheduled = null; void this.flush(); }, 3000); }
}
let active: PortalTelemetry | null = null;
export const startPortalTelemetry = (auth: Pick<Auth, "getToken">) => (active?.dispose(), active = new PortalTelemetry(auth));
export const beginPortalRoute = (path: string, inApp: boolean, started?: number, replace = false) => active?.beginRoute(path, inApp, started, replace);
export const markPortalRouteReady = () => active?.markReady();
export const stopPortalTelemetry = () => { active?.dispose(); active = null; };
function browserDeps(): Deps {
  const n = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const nav = n?.type === "back_forward" ? "back-forward" : n?.type === "reload" ? "reload" : "navigate";
  // fetch is called as deps.fetch(...): a bare `fetch` here would run with `this === deps`, which browsers reject
  // ("Illegal invocation") and flush() swallows, so nothing was ever sent (AIC-336). Keep it unbound from deps.
  return { now: () => performance.now(), fetch: (input, init) => fetch(input, init), navigationType: () => nav, vitals: browserVitals, document, window };
}

/** web-vitals observers have document lifetime; auth senders subscribe without registering duplicates. */
function documentObserver<M extends Metric>(name: "onCLS" | "onINP" | "onLCP") {
  const reported = new Set<string>();
  const subscribers = new Set<(metric: M) => void>();
  let observing = false;
  return (callback: (metric: M) => void) => {
    subscribers.add(callback);
    if (!observing) {
      observing = true;
      void import("web-vitals").then(vitals => {
        const observe = vitals[name] as (callback: (metric: M) => void, options: { reportAllChanges: false }) => void;
        observe(metric => {
          if (!subscribers.size || reported.has(metric.id)) return;
          reported.add(metric.id);
          for (const subscriber of subscribers) subscriber(metric);
        }, { reportAllChanges: false });
      }).catch(() => { observing = false; });
    }
    return () => { subscribers.delete(callback); };
  };
}
const browserVitals: Vitals = {
  onCLS: documentObserver<CLSMetric>("onCLS"), onINP: documentObserver<INPMetric>("onINP"), onLCP: documentObserver<LCPMetric>("onLCP"),
};
